import { useState } from "react";
import api from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { LoadingBlock } from "@/components/EmptyState";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Sparkles, CheckCircle2, AlertTriangle, ArrowRight, FileSearch } from "lucide-react";
import { toast } from "sonner";

export default function HealthScore() {
    const [busy, setBusy] = useState(false);
    const [result, setResult] = useState(null);
    const [missingBusy, setMissingBusy] = useState(false);
    const [missing, setMissing] = useState(null);

    const run = async () => {
        setBusy(true);
        try {
            const { data } = await api.post("/ai/health-score");
            setResult(data);
        } catch (e) {
            toast.error(e.response?.data?.detail || "AI service failed");
        } finally {
            setBusy(false);
        }
    };

    const findMissing = async () => {
        setMissingBusy(true);
        try {
            const { data } = await api.post("/ai/missing-docs");
            setMissing(data.items || []);
        } catch (e) {
            toast.error(e.response?.data?.detail || "AI service failed");
        } finally {
            setMissingBusy(false);
        }
    };

    return (
        <div data-testid="health-score-page">
            <PageHeader
                eyebrow="AI · Financial Health Score"
                title="A senior planner's eye on your vault."
                subtitle="Your Vault is read locally and never shared. The AI returns a score, three strengths, three gaps, and three actions."
                testid="hs-header"
                actions={
                    <Button onClick={run} disabled={busy} data-testid="hs-run" className="rounded-full">
                        <Sparkles className="mr-2 h-4 w-4" /> {busy ? "Analysing…" : result ? "Re-run" : "Run analysis"}
                    </Button>
                }
            />
            <div className="px-8 py-10 space-y-12">
                {busy ? <LoadingBlock label="Analysing your snapshot" /> : !result ? (
                    <div className="rounded-lg border border-dashed border-border p-10 text-center">
                        <Sparkles className="mx-auto h-8 w-8 text-accent" strokeWidth={1.5} />
                        <h3 className="mt-3 font-display text-lg font-semibold">Ready when you are</h3>
                        <p className="mt-1 text-sm text-muted-foreground max-w-md mx-auto">
                            Tap "Run analysis" to generate your Financial Health Score from the data
                            in your vault. Add more entries for richer recommendations.
                        </p>
                    </div>
                ) : (
                    <div className="grid lg:grid-cols-3 gap-8">
                        <div className="rounded-2xl border border-primary bg-primary text-primary-foreground p-8 lg:col-span-1">
                            <div className="text-xs uppercase tracking-[0.2em] font-semibold opacity-80">Score</div>
                            <div className="mt-3 font-display text-7xl font-extrabold font-mono leading-none" data-testid="hs-score">
                                {result.score}
                            </div>
                            <div className="mt-3 text-sm opacity-90">/ 100</div>
                            <p className="mt-6 font-display text-lg leading-snug" data-testid="hs-headline">
                                {result.headline}
                            </p>
                        </div>
                        <div className="lg:col-span-2 grid gap-6">
                            <Block icon={CheckCircle2} title="Strengths" items={result.strengths || []} tone="primary" testid="hs-strengths" />
                            <Block icon={AlertTriangle} title="Risks & gaps" items={result.risks || []} tone="accent" testid="hs-risks" />
                            <Block icon={ArrowRight} title="Next 3 actions" items={result.actions || []} tone="primary" testid="hs-actions" />
                        </div>
                    </div>
                )}

                {/* Missing docs detection */}
                <section className="rounded-lg border border-border bg-card p-8" data-testid="hs-missing-section">
                    <div className="flex items-end justify-between flex-wrap gap-4 mb-6">
                        <div>
                            <div className="label-tag mb-2">AI · Missing document detection</div>
                            <h2 className="font-display text-2xl font-bold tracking-tight">
                                What's likely missing from your vault.
                            </h2>
                            <p className="mt-2 text-sm text-muted-foreground max-w-xl">
                                Claude scans your snapshot and returns a prioritised checklist of documents an Indian
                                household typically needs but yours does not yet have.
                            </p>
                        </div>
                        <Button onClick={findMissing} disabled={missingBusy} variant="outline" data-testid="hs-missing-run" className="rounded-full">
                            <FileSearch className="mr-2 h-4 w-4" /> {missingBusy ? "Scanning…" : missing ? "Re-scan" : "Find missing docs"}
                        </Button>
                    </div>
                    {missingBusy ? <LoadingBlock label="Scanning vault" /> : !missing ? (
                        <p className="text-sm text-muted-foreground">Tap the button to start a scan.</p>
                    ) : missing.length === 0 ? (
                        <p className="text-sm text-muted-foreground">Your vault looks comprehensive. Nice work.</p>
                    ) : (
                        <ul className="space-y-3" data-testid="hs-missing-list">
                            {missing.map((m, i) => (
                                <li key={i} className="rounded-md border border-border p-4 flex items-start gap-4" data-testid={`missing-${i}`}>
                                    <Badge
                                        variant="outline"
                                        className={`shrink-0 capitalize ${
                                            m.priority === "high" ? "border-destructive/50 text-destructive" :
                                            m.priority === "medium" ? "border-accent/50 text-accent" :
                                            ""
                                        }`}
                                    >
                                        {m.priority}
                                    </Badge>
                                    <div className="flex-1 min-w-0">
                                        <div className="text-sm font-semibold">{m.name}</div>
                                        <div className="text-xs text-muted-foreground capitalize">{m.category}</div>
                                        <p className="mt-2 text-sm text-foreground/80">{m.why}</p>
                                    </div>
                                </li>
                            ))}
                        </ul>
                    )}
                </section>
            </div>
        </div>
    );
}

function Block({ icon: Icon, title, items, tone, testid }) {
    return (
        <section className="rounded-lg border border-border bg-card p-6" data-testid={testid}>
            <div className="flex items-center gap-3 mb-3">
                <Icon className={`h-5 w-5 ${tone === "accent" ? "text-accent" : "text-primary"}`} strokeWidth={1.75} />
                <h3 className="font-display text-lg font-semibold">{title}</h3>
            </div>
            <ul className="space-y-2 text-sm leading-relaxed">
                {items.map((s, i) => (
                    <li key={i} className="flex items-start gap-2">
                        <span className={`mt-2 h-1.5 w-1.5 rounded-full ${tone === "accent" ? "bg-accent" : "bg-primary"}`} />
                        <span>{s}</span>
                    </li>
                ))}
            </ul>
        </section>
    );
}
